import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import {
  format,
  parseISO,
  formatDistanceToNow,
  addDays,
  isToday,
  differenceInCalendarDays,
} from 'date-fns'
import { fr } from 'date-fns/locale'
import { type TFunction } from '@clofo/i18n'

export type { VariantProps } from 'class-variance-authority'

export const DATE_TIME_FORMAT_FR = 'd MMMM yyyy HH:mm' // e.g. 11 mai 2026 14:30
export const DATE_TIME_FORMAT_EN = 'MMMM d, yyyy h:mm a' // e.g. May 11, 2026 2:30 PM

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function startcase(str: string) {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim()
}

export function filesize(bytes: number): string {
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']

  if (bytes === 0) return '0 B'
  if (bytes < 1024) return `${bytes} ${sizes[0]}`

  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  const value = bytes / Math.pow(1024, i)
  const formatted = parseFloat(value.toFixed(2))

  return `${formatted} ${sizes[i]}`
}

export function truncateText(text: string, maxLength: number = 20) {
  if (text.length <= maxLength) {
    return text
  }
  return text.slice(0, maxLength) + '...'
}

/**
 * Formats a date string into a human-readable format
 * @param dateString ISO date string to format
 * @param locale Optional locale string
 * @returns Formatted date string or empty string if dateString is invalid
 */
export const formatDate = (
  date: string | Date,
  locale?: string,
  formatPattern?: string,
): string => {
  const isFrench = locale?.startsWith('fr')
  const defaultPattern = isFrench ? 'd MMMM yyyy' : 'MMMM d, yyyy'
  const pattern = formatPattern || defaultPattern

  let _date = date
  if (typeof date === 'string') {
    _date = parseISO(date)
  }

  return format(_date, pattern, {
    locale: isFrench ? fr : undefined,
  })
}

/**
 * Returns a human readable formatted date string for N days from now.
 * @param days Number of days from today
 * @param locale Optional locale string for formatting
 * @returns Formatted date string
 */
export const daysFromNow = (days: number, locale?: string): string => {
  const date = addDays(new Date(), days)
  return formatDate(date.toISOString(), locale)
}

export const formatDateShort = (dateString: string, locale?: string): string => {
  const isFrench = locale?.startsWith('fr')
  // eg: Aug 22, 2025
  const formatPattern = isFrench ? 'd MMM yyyy' : 'MMM d, yyyy'

  return format(parseISO(dateString), formatPattern, {
    locale: isFrench ? fr : undefined,
  })
}

export const formatTime = (dateString: string, formatPattern: string = 'HH:mm'): string => {
  return format(parseISO(dateString), formatPattern)
}

/**
 * Returns a string indicating days until expiration or days since expiration
 * @param expireAt ISO date string of expiration date
 * @returns Formatted string like "(in 5 days)", "(Today)", or "(3 days ago)"
 */
export const getDaysFromExpiration = (expireAt: string, t: TFunction): string => {
  const expirationDate = new Date(expireAt)

  if (isToday(expirationDate)) {
    return `(${t('dateFormats.today')})`
  }

  const diffDays = differenceInCalendarDays(expirationDate, new Date())

  if (diffDays > 0) {
    return `(${t('dateFormats.inDays', { count: diffDays })})`
  } else {
    return `(${t('dateFormats.daysAgo', { count: Math.abs(diffDays) })})`
  }
}

/**
 * Converts an HTML table element to a CSV string
 *
 * - Only includes columns with non-empty headers
 * - Finds the first row with <th> elements and uses it as the header row
 * - Skips any columns that have empty header cells
 * - For all rows, only includes cells that match the columns with headers
 * - Returns the full CSV as a string
 */
export function convertHtmlTableToCSV(table: HTMLTableElement): string {
  const data: string[] = []
  const htmlRows = Array.from(table.getElementsByTagName('tr'))

  const headerRow = htmlRows.find(row => row.querySelectorAll('th').length > 0)
  if (!headerRow) return ''

  const headerCells = Array.from(headerRow.querySelectorAll('th'))

  const includedColumnIndexes = headerCells
    .map((th, index) => (_sanitizeText(th.innerHTML).trim() ? index : -1))
    .filter(index => index !== -1)

  const headerRowData = includedColumnIndexes.map(i =>
    _sanitizeText(headerCells[i]?.innerHTML ?? ''),
  )
  data.push(headerRowData.join(','))

  for (const row of htmlRows) {
    if (row === headerRow) continue

    const cells = Array.from(row.querySelectorAll('td, th'))
    if (cells.length === 0) continue

    const rowData = includedColumnIndexes.map(i => _sanitizeText(cells[i]?.innerHTML ?? ''))
    data.push(rowData.join(','))
  }

  return data.join('\n')
}

function _sanitizeText(data: unknown): string {
  if (data === null || data === undefined) return '-'
  return String(data)
    .replace(/<\/?[^>]+(>|$)/g, '') // Remove HTML tags
    .replace(/(\r\n|\n|\r)/g, '') // Remove newlines
    .replace(/,/g, '') // Remove commas
    .trim()
}

export function downloadCSV(data: string, fileName: string | Date = new Date()): void {
  // Add BOM for Excel compatibility with french locale
  const blob = new Blob(['\uFEFF' + data], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const tempLink = document.createElement('a')
  tempLink.href = url
  tempLink.download = `${fileName}.csv`
  tempLink.style.display = 'none'
  document.body.appendChild(tempLink)
  tempLink.click()
  document.body.removeChild(tempLink)
}

export function fromSpecificDate(dateString: string, locale: string = 'en'): string {
  const date = new Date(dateString)
  const isFrench = locale.startsWith('fr')
  return formatDistanceToNow(date, {
    addSuffix: true,
    locale: isFrench ? fr : undefined,
  })
}

const EMAIL_REGEX = /^([a-zA-ZÀ-ÿ0-9_.'+-\\&])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/

export function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email)
}

/**
 * Decodes HTML entities in a string (e.g., "&lt;" becomes "<")
 * Uses string replacement for safe decoding without DOM parsing
 * @param text String containing HTML entities
 * @returns Decoded string
 */
export function decodeHtmlEntities(text: string): string {
  if (!text) return text

  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#39;/g, "'")
}
